"use client";

import {
  BriefcaseBusiness,
  CalendarCheck,
  ChevronDown,
  Store,
  Timer,
  UserRound,
  Warehouse,
  type LucideIcon,
} from "lucide-react";
import { useLocale, useTranslations } from "next-intl";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useSyncExternalStore,
} from "react";
import { buttonClassNames } from "@hisani/ui";
import { Link } from "../../../i18n/navigation";
import { localizedPath, loginPath } from "../../../i18n/paths";
import { useAuth } from "../../auth-client";
import {
  dashboardUsageSnapshot,
  parseDashboardUsageSnapshot,
  rankedQuickAccess,
  type DashboardServiceKey,
} from "./dashboard-personalization";

interface QuickAccessItem {
  readonly href:
    | "/stopwatch"
    | "/planner"
    | "/marketplace"
    | "/stable"
    | "/hire"
    | "/account";
  readonly icon: LucideIcon;
  readonly label: string;
}

export function DashboardView() {
  const auth = useAuth();
  const locale = useLocale();
  const t = useTranslations("Dashboard");
  const [quickAccessExpanded, setQuickAccessExpanded] = useState(true);
  const accountId =
    auth.status === "authenticated" ? auth.account.id : undefined;
  const subscribeToUsage = useCallback((onStoreChange: () => void) => {
    const handleStorage = () => onStoreChange();
    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, []);
  const getUsageSnapshot = useCallback(
    () =>
      accountId === undefined
        ? null
        : dashboardUsageSnapshot(window.localStorage, accountId),
    [accountId],
  );
  const usageSnapshot = useSyncExternalStore(
    subscribeToUsage,
    getUsageSnapshot,
    emptyUsageSnapshot,
  );
  const usage = useMemo(
    () => parseDashboardUsageSnapshot(usageSnapshot),
    [usageSnapshot],
  );

  useEffect(() => {
    if (auth.status === "anonymous") {
      window.location.replace(loginPath(locale, "/dashboard"));
    } else if (
      auth.status === "authenticated" &&
      !auth.account.profileCompleted
    ) {
      window.location.replace(localizedPath(locale, "/onboarding"));
    }
  }, [auth, locale]);

  if (auth.status === "loading" || auth.status === "unavailable") {
    return <DashboardLoading />;
  }
  if (auth.status !== "authenticated") return null;

  const displayName =
    auth.account.displayName ?? auth.account.primaryRole ?? t("welcomeDefault");
  const quickAccessByKey: Record<DashboardServiceKey, QuickAccessItem> = {
    stopwatch: {
      href: "/stopwatch",
      icon: Timer,
      label: t("quickAccess.items.stopwatch"),
    },
    planner: {
      href: "/planner",
      icon: CalendarCheck,
      label: t("quickAccess.items.planner"),
    },
    marketplace: {
      href: "/marketplace",
      icon: Store,
      label: t("quickAccess.items.marketplace"),
    },
    stable: {
      href: "/stable",
      icon: Warehouse,
      label: t("quickAccess.items.stable"),
    },
    hire: {
      href: "/hire",
      icon: BriefcaseBusiness,
      label: t("quickAccess.items.hire"),
    },
    account: {
      href: "/account",
      icon: UserRound,
      label: t("quickAccess.items.account"),
    },
  };
  const quickAccess = rankedQuickAccess(auth.account.primaryRole, usage).map(
    (key) => quickAccessByKey[key],
  );
  const today = new Intl.DateTimeFormat(locale, {
    weekday: "long",
    day: "numeric",
    month: "long",
  }).format(new Date());

  return (
    <div className="flex flex-1 flex-col bg-surface text-primary">
      <div className="mx-auto flex w-full max-w-5xl flex-1 flex-col px-[var(--space-page-inline)] pt-0 pb-4 sm:py-7">
        <header>
          <p className="text-sm font-semibold text-secondary">{today}</p>
          <h1 className="mt-1 text-heading-xlarge text-primary">
            {t("welcome", { name: displayName })}
          </h1>
        </header>

        <section
          aria-labelledby="dashboard-quick-access-heading"
          className="mt-6"
        >
          <h2 className="text-heading-medium">
            <button
              type="button"
              className="flex min-h-11 w-full items-center justify-between gap-3 rounded-md bg-transparent text-start text-primary hover:bg-transparent active:bg-transparent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus focus-visible:ring-offset-2 focus-visible:ring-offset-canvas"
              aria-expanded={quickAccessExpanded}
              aria-controls="dashboard-quick-access-items"
              onClick={() => setQuickAccessExpanded((expanded) => !expanded)}
            >
              <span id="dashboard-quick-access-heading">
                {t("quickAccess.heading")}
              </span>
              <ChevronDown
                className={`size-5 transition-transform duration-[var(--motion-duration-fast)] motion-reduce:transition-none ${
                  quickAccessExpanded ? "rotate-180" : ""
                }`}
                aria-hidden="true"
              />
            </button>
          </h2>
          <div
            id="dashboard-quick-access-items"
            hidden={!quickAccessExpanded}
            className="mt-2 flex flex-wrap gap-2"
          >
            {quickAccess.map((item) => {
              const Icon = item.icon;
              return (
                <Link
                  key={item.href}
                  href={item.href}
                  className={buttonClassNames({
                    variant: "accent",
                    size: "sm",
                    className: "gap-2 px-4",
                  })}
                >
                  <Icon className="size-4" aria-hidden="true" />
                  {item.label}
                </Link>
              );
            })}
          </div>
        </section>
      </div>
    </div>
  );
}

function emptyUsageSnapshot(): null {
  return null;
}

function DashboardLoading() {
  return (
    <div className="mx-auto flex w-full max-w-5xl flex-1 flex-col bg-surface px-[var(--space-page-inline)] pt-0 pb-4 sm:py-7">
      <div className="h-16 animate-pulse rounded-lg bg-subtle motion-reduce:animate-none" />
      <div className="mt-6 h-24 animate-pulse rounded-lg bg-subtle motion-reduce:animate-none" />
    </div>
  );
}
